import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Box, Typography, Grid, FormControlLabel, Checkbox, Button } from '@mui/material';
import { EventDocumentTemplate } from '../../../../types';
import {
	eventsService,
	getEventDocumentTemplatePdfUrl,
} from '../../../../core/services/eventsService';
import { EventDocumentPdfEditorModal } from './EventDocumentPdfEditorModal';
import styles from '../SchedulerSetting.module.scss';

interface EditBanquetEventOrderProps {
	document: EventDocumentTemplate;
	existingNames: string[];
	handleBackClick?: () => void;
	onDocumentUpdated?: () => void;
}

/**
 * Same layout as onboarding EditDocument: Back / Save, file path, name, sort order.
 * Event PDF templates only — no document type field.
 */
const EditBanquetEventOrder: React.FC<EditBanquetEventOrderProps> = ({
	document,
	existingNames,
	handleBackClick,
	onDocumentUpdated,
}) => {
	const [name, setName] = useState(document.name);
	const [sortOrder, setSortOrder] = useState(document.sortOrder);
	/** API may omit `active`; treat as true unless explicitly false */
	const [active, setActive] = useState(() => document.active ?? true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);

	const [pdfEditorOpen, setPdfEditorOpen] = useState(false);
	const [pdfEditorUrl, setPdfEditorUrl] = useState<string | null>(null);
	const [isReplacingPdf, setIsReplacingPdf] = useState(false);

	const closePdfEditor = useCallback(() => {
		setPdfEditorOpen(false);
		setPdfEditorUrl(null);
	}, []);

	const openPdfEditor = () => {
		const url = getEventDocumentTemplatePdfUrl(document.filePath || '');
		if (!url) {
			setError('No PDF file is available to edit yet.');
			return;
		}
		setPdfEditorUrl(url);
		setPdfEditorOpen(true);
		setError(null);
	};

	const handlePdfEditorSave = async (file: File) => {
		setIsReplacingPdf(true);
		try {
			await eventsService.replaceEventDocumentTemplateFile(document.id, file);
			onDocumentUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 600);
		} catch (err: any) {
			console.error('Replace PDF error:', err);
			setError(
				err?.message ||
					'Could not save PDF. If this persists, the replace-file API may need to be enabled on the server.'
			);
			closePdfEditor();
		} finally {
			setIsReplacingPdf(false);
		}
	};

	useEffect(() => {
		setName(document.name);
		setSortOrder(document.sortOrder);
		setActive(document.active ?? true);
		setError(null);
		setSuccess(null);
	}, [document.id, document.name, document.sortOrder, document.active]);

	const handleSortOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value, 10);
		if (!isNaN(value) && value >= 1 && value <= 50) {
			setSortOrder(value);
			setError(null);
		}
	};

	const handleSubmit = async () => {
		setError(null);
		setSuccess(null);

		const trimmed = name.trim();
		if (!trimmed) {
			setError('Name is required');
			return;
		}

		if (existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
			setError(`A document named "${trimmed}" already exists. Names must be unique.`);
			return;
		}

		if (sortOrder < 1 || sortOrder > 50) {
			setError('Sort order must be between 1 and 50');
			return;
		}

		if (!document.id) {
			setError('Document ID is missing');
			return;
		}

		setIsSubmitting(true);

		try {
			await eventsService.updateEventDocumentTemplate(document, {
				name: trimmed,
				sortOrder,
				active,
			});
			setSuccess('Document updated successfully!');
			setTimeout(() => {
				onDocumentUpdated?.();
				handleBackClick?.();
			}, 800);
		} catch (err: any) {
			console.error('Error updating document:', err);
			setError(err?.message || 'Failed to update document');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.createTimeOffReason}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick}>
						Back
					</Button>
					<Button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Edit Banquet Event Order</h1>
				</Grid>
			</Grid>

			<Box sx={{ padding: 3 }}>
				{error && (
					<Typography color="error" sx={{ mb: 2 }}>
						{error}
					</Typography>
				)}
				{success && (
					<Typography color="success.main" sx={{ mb: 2 }}>
						{success}
					</Typography>
				)}

				<Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						File path
					</Typography>
					<Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
						{document.filePath || 'N/A'}
					</Typography>
					<Button
						className={styles.submitButton}
						sx={{ mt: 2 }}
						onClick={openPdfEditor}
						disabled={!document.filePath || isReplacingPdf}
					>
						{isReplacingPdf ? 'Saving PDF...' : 'Edit PDF on screen'}
					</Button>
				</Box>

				<TextField
					label="Name"
					value={name}
					onChange={(e) => {
						setName(e.target.value);
						setError(null);
					}}
					fullWidth
					required
					helperText="Label shown for this banquet event order document"
					sx={{ mb: 3 }}
					inputProps={{
						maxLength: 100,
					}}
				/>

				<TextField
					label="Sort order"
					type="number"
					value={sortOrder}
					onChange={handleSortOrderChange}
					fullWidth
					required
					helperText="Order in which the document appears (1–50)"
					sx={{ mb: 2 }}
					inputProps={{
						min: 1,
						max: 50,
					}}
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={active}
							onChange={(_, checked) => {
								setActive(checked);
								setError(null);
							}}
							color="primary"
						/>
					}
					label="Active"
					sx={{ mb: 3, display: 'block', alignItems: 'flex-start' }}
				/>
			</Box>

			<EventDocumentPdfEditorModal
				open={pdfEditorOpen}
				onClose={closePdfEditor}
				pdfUrl={pdfEditorUrl}
				documentTitle={document.name}
				onSave={handlePdfEditorSave}
			/>
		</div>
	);
};

export default EditBanquetEventOrder;
